import React, { useState, useEffect } from 'react';
import '../../styles/EditStudentPopupst.css';
import Select from 'react-select';

const EditStudentPopup = ({ isOpen, onClose, student, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        email_Id: '',
        contactNo: '',
        ParentNo: '',
        address: '',
        learningMode: '',
        staffAssigned: '',
        courseType: '',
        course: '',
        courseFees: '',
        discountAmount: '0',
        feesCollected: '0',
        pendingFees: '',
        pendingFees2: '0',
        pendingFees3: '0',
        pendingFees4: '0',
        studentProgressStatus: '',
        adminbranch: ''
    });

    const [originalCourseFees, setOriginalCourseFees] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [courses, setCourses] = useState([]);
    const [courseTypes, setCourseTypes] = useState([]);
    const [trainers, setTrainers] = useState([]);
    const [userBranch, setUserBranch] = useState('');

    // Get user branch from localStorage or token
    useEffect(() => {
        const getUserBranch = () => {
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    // Decode JWT token to get branch information
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    const branch = payload.branch || '';
                    setUserBranch(branch);
                    return branch;
                }
            } catch (error) {
                console.error('Error decoding token:', error);
            }
            return '';
        };

        getUserBranch();
    }, []);

    // Fetch trainers from API when component mounts and branch is available
    useEffect(() => {
        const fetchTrainers = async () => {
            if (!formData.adminbranch) return;

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/trainers?branch=${formData.adminbranch}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch trainers');
                }
                const data = await response.json();
                setTrainers(data);
            } catch (err) {
                setError('Failed to load trainer data: ' + err.message);
            }
        };

        fetchTrainers();
    }, [formData.adminbranch]);

    // Fetch courses from API when component mounts
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/course`);
                if (!response.ok) {
                    throw new Error('Failed to fetch courses');
                }
                const data = await response.json();
                setCourses(data);

                // Extract unique course types
                const uniqueTypes = [...new Set(data.map(course => course.courseType))];
                setCourseTypes(uniqueTypes);
            } catch (err) {
                setError('Failed to load course data: ' + err.message);
            }
        };

        fetchCourses();
    }, []);

    // Populate form with student data when component mounts
    useEffect(() => {
        if (student) {
            setFormData({
                name: student.name || '',
                email_Id: student.email_Id || '',
                contactNo: student.contactNo || '',
                ParentNo: student.ParentNo || '',
                address: student.address || '',
                learningMode: student.learningMode || '',
                staffAssigned: student.staffAssigned || '',
                courseType: student.courseType || '',
                course: student.course || '',
                courseFees: student.courseFees || '',
                discountAmount: student.discountAmount || '0',
                feesCollected: student.feesCollected || '0',
                pendingFees: student.pendingFees || '',
                pendingFees2: student.pendingFees2 || '0',
                pendingFees3: student.pendingFees3 || '0',
                pendingFees4: student.pendingFees4 || '0',
                studentProgressStatus: student.studentProgressStatus || '',
                adminbranch: student.adminbranch || userBranch || ''
            });

            // Store original course fees for comparison
            setOriginalCourseFees(student.courseFees || '');
        }
    }, [student, userBranch]);

    // Calculate pending fees whenever relevant fields change
    useEffect(() => {
        const calculatePendingFees = () => {
            const fees = parseFloat(formData.courseFees) || 0;
            const discount = parseFloat(formData.discountAmount) || 0;
            const collected = parseFloat(formData.feesCollected) || 0;

            const pending = fees - (collected + discount);

            // Check if course fees changed compared to the original
            if (formData.courseFees !== originalCourseFees) {
                // If course fees changed, set additional pending fees fields to 0
                setFormData(prevState => ({
                    ...prevState,
                    pendingFees: pending.toString(),
                    pendingFees2: '0',
                    pendingFees3: '0',
                    pendingFees4: '0'
                }));
            } else {
                // If course fees didn't change, only update the main pendingFees
                setFormData(prevState => ({
                    ...prevState,
                    pendingFees: pending.toString()
                }));
            }
        };

        if (formData.courseFees) {
            calculatePendingFees();
        }
    }, [formData.courseFees, formData.discountAmount, formData.feesCollected, originalCourseFees]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'courseType') {
            // Update course type and clear course selection
            setFormData({
                ...formData,
                courseType: value,
                course: '',
                courseFees: ''
            });
        } else if (name === 'course') {
            // Find the selected course and update course fees
            const selectedCourse = courses.find(course => course.courseName === value);
            if (selectedCourse) {
                setFormData({
                    ...formData,
                    course: value,
                    courseFees: selectedCourse.courseFees,
                    courseType: selectedCourse.courseType
                });
            } else {
                setFormData({
                    ...formData,
                    [name]: value
                });
            }
        } else if (name === 'adminbranch') {
            // When branch changes, reset staff assigned and fetch new trainers
            setFormData({
                ...formData,
                adminbranch: value,
                staffAssigned: ''
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('token');

            const response = await fetch(`${process.env.REACT_APP_API_URL}/update/${student.id || student._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update student information');
            }

            const updatedStudent = await response.json();
            setSuccess('Student information updated successfully!');

            // Pass the updated student data back to the parent component
            setTimeout(() => {
                onSave({
                    ...student,  // Keep original properties
                    ...updatedStudent,  // Apply updates from the API
                    ...formData  // Ensure all form data is included
                });
            }, 1000);
        } catch (err) {
            setError(err.message || 'An error occurred while updating student information');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess('');
            }, 1000);

            return () => clearTimeout(timer); // Clean up on unmount or success change
        }
    }, [success]);

    // If the modal is not open or no student is provided, don't render anything
    if (!isOpen || !student) return null;

    return (
        <div className="payment-modal-overlay">
            <div className="payment-modal">
                <div className="payment-modal-header">
                    <h2>Edit Student Information</h2>
                    <button
                        className="modal-close-btn"
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                <div className="payment-modal-content">
                   

                    <div className="payment-field-group">
                        <label>Name:</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            readOnly
                            className="readonly-field"
                            required
                        />
                    </div>

                    <div className="payment-field-group">
                        <label>Email ID:</label>
                        <input
                            type="email"
                            name="email_Id"
                            value={formData.email_Id}
                            onChange={handleChange}
                            readOnly
                            className="readonly-field"
                            required
                        />
                    </div>
                    

                    <div className="payment-field-group">
                        <label>Contact Number:</label>
                        <input
                            type="tel"
                            name="contactNo"
                            value={formData.contactNo}
                            onChange={handleChange}
                            className="editable-field"
                            required
                        />
                    </div>

                    <div className="payment-field-group">
                        <label>Parent Number:</label>
                        <input
                            type="tel"
                            name="ParentNo"
                            value={formData.ParentNo}
                            onChange={handleChange}
                            className="editable-field"
                        />
                    </div>

                    <div className="payment-field-group">
                        <label>Address:</label>
                        <textarea
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            className="editable-field"
                        />
                    </div>

                    <div className="payment-field-group">
                        <label>Learning Mode:</label>
                        <select
                            name="learningMode"
                            value={formData.learningMode}
                            onChange={handleChange}
                            className="editable-field"
                        >
                            <option value="">Select Mode</option>
                            <option value="online">Online</option>
                            <option value="offline">Offline</option>
                            <option value="Hybrid">Hybrid</option>
                        </select>
                    </div>

                    <div className="payment-field-group">
                        <label>Staff Assigned:</label>
                        <select
                            name="staffAssigned"
                            value={formData.staffAssigned}
                            onChange={handleChange}
                            className="editable-field"
                            disabled={!formData.adminbranch}
                        >
                            <option value="">Select Staff</option>
                            {trainers.map((trainer) => (
                                <option key={trainer.emp_id} value={trainer.emp_name}>
                                    {trainer.emp_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="payment-field-group">
                        <label>Course Type:</label>
                        <input
                            type="text"
                            name="courseType"
                            value={formData.courseType}
                            readOnly
                            className="readonly-field"
                        />
                    </div>

                    <div className="payment-field-group">
                        <label>Course Name:</label>
                        <Select
                            name="course"
                            value={
                                courses
                                    .filter(course => !formData.courseType || course.courseType === formData.courseType)
                                    .map(course => ({
                                        value: course.courseName,
                                        label: course.courseName
                                    }))
                                    .find(option => option.value === formData.course) || null
                            }
                            onChange={(selectedOption) => {
                                const selectedCourse = courses.find(c => c.courseName === selectedOption.value);
                                if (selectedCourse) {
                                    setFormData(prev => ({
                                        ...prev,
                                        course: selectedOption.value,
                                        courseFees: selectedCourse.courseFees,
                                        courseType: selectedCourse.courseType
                                    }));
                                }
                            }}
                            options={courses
                                .filter(course => !formData.courseType || course.courseType === formData.courseType)
                                .map(course => ({
                                    value: course.courseName,
                                    label: course.courseName
                                }))
                            }
                            classNamePrefix="react-select"
                            placeholder="Select or search course..."
                            isSearchable
                        />
                    </div>

                    <div className="payment-field-group">
                        <label>Course Fees:</label>
                        <input
                            type="number"
                            name="courseFees"
                            value={formData.courseFees}
                            onChange={handleChange}
                            readOnly
                            className="readonly-field"
                        />
                    </div>

                    <div className="payment-field-group">
                        <label>Fees Collected:</label>
                        <input
                            type="number"
                            name="feesCollected"
                            value={formData.feesCollected}
                            onChange={handleChange}
                            readOnly
                            className="readonly-field"
                            required
                        />
                    </div>

                    <div className="payment-field-group">
                        <label>Discount Amount:</label>
                        <input
                            type="number"
                            name="discountAmount"
                            value={formData.discountAmount}
                            onChange={handleChange}
                            className="editable-field"
                        />
                    </div>

                    <div className="payment-field-group">
                        <label>Pending Fees:</label>
                        <input
                            type="number"
                            name="pendingFees"
                            value={formData.pendingFees}
                            onChange={handleChange}
                            readOnly
                            className="readonly-field"
                        />
                    </div>

                    {/* Hidden fields for pendingFees2, pendingFees3, pendingFees4 */}
                    <input
                        type="hidden"
                        name="pendingFees2"
                        value={formData.pendingFees2}
                    />
                    <input
                        type="hidden"
                        name="pendingFees3"
                        value={formData.pendingFees3}
                    />
                    <input
                        type="hidden"
                        name="pendingFees4"
                        value={formData.pendingFees4}
                    />

                    <div className="payment-field-group">
                        <label>Student Progress Status:</label>
                        <select
                            name="studentProgressStatus"
                            value={formData.studentProgressStatus}
                            onChange={handleChange}
                            className="editable-field"
                            required
                        >
                            <option value="">Select Status</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Course and Certified Completed">Course and Certified Completed</option>
                            <option value="Course Completed">Course Completed</option>
                            <option value="Course Completed, Certified, and Successfully Placed">Course Completed, Certified, and Successfully Placed</option>                           
                            <option value="Discontinued">Discontinued</option>
                            <option value="Discontinued & Refunded">Discontinued & Refunded</option>
                            <option value="Inactive">Inactive</option>
                            <option value="Terminated">Terminated</option>
                            <option value="Terminated & Refunded">Terminated & Refunded</option>
                            <option value="Successfully Placed">Successfully Placed</option>
                        </select>
                    </div>

                    {error && <div className="error-message">{error}</div>}
                    {success && <div className="success-message">{success}</div>}
                </div>

                <div className="payment-modal-actions">
                    <button
                        className="btn-submit"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                        className="btn-cancel"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditStudentPopup;